import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Button, Grid, Dimmer, Loader } from "semantic-ui-react";
import { EditorState } from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';
import { toast } from 'react-toastify';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AdminSidebar from '../Common/SharedComponents/AdminSidebar';
import RichTextEditor, { decoratorPlugin } from '../Common/SharedComponents/RichTextEditor';
import AppContext, { token_config } from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

const ReplyMessage: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.REPLY_MESSAGE)) {
        navigate('/');
      }
      if (userInfo.IsAllOffices !== "True") {
        fetchData();
      } else {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const { themeVersion } = useContext(AppContext);
  const isDarkTheme = themeVersion === "black" ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditorFocus, setIsEditorFocus] = useState<boolean>(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [replyMessage, setReplyMessage] = useState<string>('');

  useEffect(() => {
    document.title = 'Reply Message - Adjure';
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get('adminsetting/GetReplyMessage').then(({ data, status }) => {
        if (status === 200 || status === 204) {
          setIsLoading(false);
          setReplyMessage(data);
          const contentState = stateFromHTML(data || '');
          const newEditorState = EditorState.createWithContent(contentState, decoratorPlugin());
          setEditorState(newEditorState);
        }
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleRichEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const currentContent = newEditorState.getCurrentContent();
    setReplyMessage(stateToHTML(currentContent));
  };

  const handleRichEmailEditorFocus = () => {
    if (!isEditorFocus) {
      // Convert the plain text content to EditorState and update the RichTextEditor
      const contentState = stateFromHTML(replyMessage || '');
      const newEditorState = EditorState.createWithContent(contentState, decoratorPlugin());
      setEditorState(newEditorState);
    }
    setIsEditorFocus(true);
  };

  const handleSubmit = async () => {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    await axiosInstance.post('adminsetting/UpdateReplyMessage', JSON.stringify(replyMessage), config).then(({ data, status }) => {
      if (status === 200) {
        toast.success('Reply message updated successfully');
      }
    });
  }

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Reply Message</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className="section__body">
        <Dimmer.Dimmable as="div" dimmed={isLoading}>
          <Dimmer active={isLoading} inverted={true}>
            <Loader>Loading</Loader>
          </Dimmer>
          <Grid padded>
            <Grid.Row>
              <Grid.Column mobile={16} tablet={3} computer={3}>
                <AdminSidebar />
              </Grid.Column>
              <Grid.Column mobile={16} tablet={6} computer={6}>
                <RichTextEditor onFocus={handleRichEmailEditorFocus} isFocus={isEditorFocus} onChange={handleRichEditorChange} editorState={editorState} rows={6} theme={isDarkTheme ? 'dark' : 'light'} showHtml={replyMessage} showImageUpload />
                <Button type="submit" onClick={handleSubmit} className='violet right floated button mt-2' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>Save</Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Dimmer.Dimmable>
      </div>
    </section>
  );
}

export default ReplyMessage;